/**
 * DemoStateBanner — passive top-nav indicator that reads the public
 * `/api/system/demo-state` endpoint and surfaces the current environment
 * flavor to visitors.
 *
 * Ops — Cleanup Pass Phase 3 (Feb 2026).
 *
 * States:
 *   * clean_slate → amber pill "Demo · Clean Slate" (tooltip: "No bookings or sessions loaded. Reference data only.")
 *   * seeded      → blue pill  "Demo · Seeded"      (tooltip: "Sample bookings loaded for demo purposes.")
 *   * live        → nothing rendered (production — stay quiet)
 *
 * Fetches on mount, refreshes every 60 s. Fails silently on any error
 * (network, 500, bad JSON) — must never break the surrounding app.
 *
 * The pure `<DemoStatePill state="...">` presentational component is exported
 * so unit tests can render it directly without the fetch/effect stack.
 */
import { useEffect, useState } from "react";

const REFRESH_MS = 60_000;

const COPY = {
  clean_slate: {
    label: "Demo · Clean Slate",
    tooltip: "No bookings or sessions loaded. Reference data only.",
    className: "bg-amber-950/40 text-amber-300 border-amber-700/70",
  },
  seeded: {
    label: "Demo · Seeded",
    tooltip: "Sample bookings loaded for demo purposes.",
    className: "bg-sky-950/40 text-sky-300 border-sky-700/70",
  },
};

/**
 * Pure presentational pill. Returns `null` for anything not in COPY (including
 * `"live"`, `null`, `undefined`, and any unrecognized values). Zero side effects
 * — safe to unit-test with any renderer.
 */
export function DemoStatePill({ state }) {
  const copy = COPY[state];
  if (!copy) return null;
  return (
    <div
      data-testid="demo-state-banner"
      data-state={state}
      title={copy.tooltip}
      className={`inline-flex items-center gap-1.5 label-tag px-2 py-0.5 rounded-sm border font-medium ${copy.className}`}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-current opacity-70" aria-hidden />
      {copy.label}
    </div>
  );
}

export function DemoStateBanner({ apiBase }) {
  const [state, setState] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const base = (apiBase || process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
    if (!base) return undefined;

    const fetchState = async () => {
      try {
        const r = await fetch(`${base}/api/system/demo-state`);
        if (!r.ok) return;
        const body = await r.json();
        if (!cancelled) setState(body?.state ?? null);
      } catch {
        // Silent failure — banner is passive, must never break the app.
      }
    };

    fetchState();
    const id = setInterval(fetchState, REFRESH_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, [apiBase]);

  return <DemoStatePill state={state} />;
}

export default DemoStateBanner;
